/**
 * Подсказка «Возможно, вы имели в виду…» для введённой должности (names-and-positions-normalization,
 * согласовано с заказчицей 15.10.2026): если такой проверенной должности в справочнике нет, а есть
 * очень похожая — предлагаем её («тактористы» → «Тракторист»). Ввод не блокируется: это подсказка.
 *
 * Сравнение — без учёта регистра и «ё/е», латинские буквы-двойники считаются русскими
 * («Teхник» с латинской «T» = «Техник»), пробелы и дефисы — как в справочнике. Расстояние —
 * Дамерау–Левенштейн (замена, вставка, удаление, перестановка соседних букв): до одной правки для
 * слов до шести букв, до двух — для более длинных. Цифры должны совпадать: «Слесарь 4 разряда» и
 * «Слесарь 5 разряда» — разные должности, а не опечатка.
 */
import { isOfferedPosition, type Position } from '@/lib/api'
import { cleanPositionName } from '@/lib/name-case'

/** Сколько вариантов показываем под полем. */
export const MAX_SUGGESTIONS = 3

/** Латинские буквы, которые выглядят как русские (после перевода в нижний регистр). */
const LATIN_TWINS: Record<string, string> = {
  a: 'а', b: 'в', c: 'с', e: 'е', h: 'н', k: 'к', m: 'м', o: 'о', p: 'р', t: 'т', x: 'х', y: 'у',
}

/** Ключ сравнения: очищено, нижний регистр, «ё» = «е», латинские двойники — русскими. */
export function suggestKey(s: string): string {
  return Array.from(cleanPositionName(s).toLowerCase().replaceAll('ё', 'е'))
    .map((ch) => LATIN_TWINS[ch] ?? ch)
    .join('')
}

const digitsOf = (s: string) => s.replace(/\D/g, '')

/**
 * Расстояние Дамерау–Левенштейна (вариант «оптимальное выравнивание строк») с ранним выходом:
 * больше `limit` — возвращает `limit + 1`.
 */
export function editDistance(a: string, b: string, limit: number): number {
  const s = Array.from(a)
  const t = Array.from(b)
  if (Math.abs(s.length - t.length) > limit) return limit + 1
  let prev2: number[] = []
  let prev = Array.from({ length: t.length + 1 }, (_, j) => j)
  for (let i = 1; i <= s.length; i++) {
    const cur = [i]
    let rowMin = i
    for (let j = 1; j <= t.length; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
      if (i > 1 && j > 1 && s[i - 1] === t[j - 2] && s[i - 2] === t[j - 1]) {
        v = Math.min(v, prev2[j - 2] + 1)
      }
      cur.push(v)
      if (v < rowMin) rowMin = v
    }
    if (rowMin > limit) return limit + 1
    prev2 = prev
    prev = cur
  }
  return prev[t.length]
}

interface Candidate {
  p: Position
  key: string
  len: number
  digits: string
  exact: string
}

/** Ключи справочника считаются один раз на загруженный список, а не на каждый символ ввода. */
const candidatesCache = new WeakMap<Position[], Candidate[]>()

function candidatesOf(positions: Position[]): Candidate[] {
  let list = candidatesCache.get(positions)
  if (!list) {
    list = positions
      .filter((p) => isOfferedPosition(p) && !p.name.startsWith('('))
      .map((p) => {
        const key = suggestKey(p.name)
        return { p, key, len: Array.from(key).length, digits: digitsOf(key), exact: cleanPositionName(p.name).toLowerCase() }
      })
    candidatesCache.set(positions, list)
  }
  return list
}

/**
 * Похожие проверенные и не скрытые должности для введённого текста (до `MAX_SUGGESTIONS`, ближайшие первыми).
 * Пусто, если текст короче трёх букв или совпадает с предлагаемой должностью без учёта регистра.
 */
export function suggestSimilarPositions(text: string, positions: Position[]): Position[] {
  const key = suggestKey(text)
  if (Array.from(key.replace(/[^\p{L}]/gu, '')).length < 3) return []
  const candidates = candidatesOf(positions)
  const exact = cleanPositionName(text).toLowerCase()
  if (candidates.some((c) => c.exact === exact)) return []
  const len = Array.from(key).length
  const limit = len <= 6 ? 1 : 2
  const digits = digitsOf(key)
  const found: { p: Position; d: number }[] = []
  for (const c of candidates) {
    if (c.digits !== digits || Math.abs(c.len - len) > limit) continue
    const d = editDistance(key, c.key, limit)
    if (d <= limit) found.push({ p: c.p, d })
  }
  found.sort((x, y) => x.d - y.d || x.p.name.localeCompare(y.p.name, 'ru'))
  return found.slice(0, MAX_SUGGESTIONS).map((f) => f.p)
}
